import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Camera, X, Trash, Plus, Star, Clock, MapPin, CalendarBlank, Trophy } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getCategoryLabel } from '../lib/categories';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PastSaleCard({ sale, onDelete, isOwner }) {
  const placeholderImg = 'https://images.pexels.com/photos/4923440/pexels-photo-4923440.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600';
  return (
    <div className="bg-white border-2 border-[#004E89] rounded-2xl overflow-hidden shadow-[4px_4px_0px_#004E89] hover:-translate-y-1 transition-transform" data-testid={`past-sale-${sale.id}`}>
      <div className="h-44">
        {sale.photos?.length > 0 ? (
          <img src={`${API}/files/${sale.photos[0]}`} alt={sale.title} className="w-full h-full object-cover"
            onError={(e) => { e.target.src = placeholderImg; }} />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#004E89] to-[#006BB6] flex items-center justify-center">
            <Trophy size={48} weight="duotone" className="text-[#FFD166]" />
          </div>
        )}
      </div>
      <div className="p-4 space-y-2">
        <span className={`tag tag-${sale.category === 'business_closing' ? 'business' : sale.category} text-[10px]`}>
          {getCategoryLabel(sale.category)}
        </span>
        <h3 className="text-base font-bold text-[#004E89] line-clamp-1" style={{ fontFamily: 'Outfit' }}>{sale.title}</h3>
        {(sale.city || sale.state) && (
          <p className="text-xs text-[#5C7A99] flex items-center gap-1">
            <MapPin size={12} weight="bold" /> {sale.city}{sale.city && sale.state ? ', ' : ''}{sale.state}
          </p>
        )}
        {sale.sale_date && (
          <p className="text-xs text-[#5C7A99] flex items-center gap-1">
            <CalendarBlank size={12} weight="bold" /> {new Date(sale.sale_date).toLocaleDateString()}
          </p>
        )}
        {sale.highlights && (
          <p className="text-xs text-[#5C7A99] line-clamp-2 italic">{sale.highlights}</p>
        )}
        <p className="text-xs text-[#5C7A99]">by <span className="font-bold text-[#004E89]">{sale.creator_name}</span></p>
        {isOwner && (
          <button onClick={() => onDelete(sale.id)}
            className="mt-2 text-xs text-red-500 hover:text-red-700 font-bold flex items-center gap-1"
            data-testid={`delete-past-sale-${sale.id}`}
          >
            <Trash size={12} weight="bold" /> Remove
          </button>
        )}
      </div>
    </div>
  );
}

export default function PastSalesPage() {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [pastSales, setPastSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', category: 'estate',
    city: '', state: '', sale_date: '', highlights: '',
  });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const isPremium = user?.subscription === 'premium' || user?.role === 'admin';

  const fetchPastSales = async () => {
    try {
      const url = user ? `${API}/past-sales/mine` : `${API}/past-sales`;
      const { data } = await axios.get(url, { withCredentials: true });
      setPastSales(data.past_sales || []);
    } catch {
      // fallback to public
      try {
        const { data } = await axios.get(`${API}/past-sales`);
        setPastSales(data.past_sales || []);
      } catch { /* ignore */ }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPastSales(); }, []);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    setPhotos(prev => [...prev, ...files.map(f => ({ file: f, preview: URL.createObjectURL(f), path: null }))]);
  };

  const removePhoto = (idx) => {
    setPhotos(prev => {
      const u = [...prev];
      URL.revokeObjectURL(u[idx].preview);
      u.splice(idx, 1);
      return u;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error('Title is required'); return; }
    setSubmitting(true);
    try {
      // Upload photos
      const paths = [];
      for (const p of photos) {
        if (p.path) { paths.push(p.path); continue; }
        const fd = new FormData();
        fd.append('file', p.file);
        const { data } = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
        paths.push(data.path);
      }
      await axios.post(`${API}/past-sales`, { ...form, photo_paths: paths }, { withCredentials: true });
      toast.success('Past sale added!');
      setShowForm(false);
      setForm({ title: '', description: '', category: 'estate', city: '', state: '', sale_date: '', highlights: '' });
      setPhotos([]);
      fetchPastSales();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add past sale');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this past sale?')) return;
    try {
      await axios.delete(`${API}/past-sales/${id}`, { withCredentials: true });
      setPastSales(prev => prev.filter(s => s.id !== id));
      toast.success('Removed');
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F9F9F6] px-4 py-8" data-testid="past-sales-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl sm:text-4xl font-black text-[#004E89]" style={{ fontFamily: 'Outfit' }}>Past Sales</h1>
              <span className="bg-[#FFD166] text-[#004E89] text-[10px] font-bold px-3 py-1 rounded-full border-2 border-[#004E89] shadow-[2px_2px_0px_#004E89] uppercase tracking-wider">
                Pro Feature
              </span>
            </div>
            <p className="text-[#5C7A99]">Showcase your completed sales and build your reputation</p>
          </div>
          {isPremium && (
            <button onClick={() => setShowForm(!showForm)} className="btn-primary py-2.5 px-5 flex items-center gap-2" data-testid="add-past-sale-btn">
              <Plus size={18} weight="bold" /> Add Past Sale
            </button>
          )}
        </div>

        {/* Upgrade banner for non-pro */}
        {!isPremium && (
          <div className="bg-[#004E89] border-2 border-[#004E89] rounded-2xl p-8 mb-8 text-center shadow-[6px_6px_0px_#FFD166]" data-testid="upgrade-banner">
            <Star size={40} weight="fill" className="text-[#FFD166] mx-auto mb-4" />
            <h2 className="text-2xl font-black text-white mb-2" style={{ fontFamily: 'Outfit' }}>Pro Feature</h2>
            <p className="text-[#B8D4E8] mb-6 max-w-md mx-auto">
              Upgrade to Pro to showcase your past sales and build trust with buyers. Show them your track record!
            </p>
            <Link to="/pricing" data-testid="upgrade-to-pro-btn">
              <button className="btn-secondary py-3 px-8">Upgrade to Pro — $50/mo</button>
            </Link>
          </div>
        )}

        {/* Add Past Sale Form */}
        {showForm && isPremium && (
          <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89] mb-8 fade-in-up" data-testid="past-sale-form">
            <h2 className="text-xl font-bold text-[#004E89] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Trophy size={24} weight="duotone" />
              Add a Past Sale
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Title *</label>
                  <input value={form.title} onChange={(e) => updateField('title', e.target.value)}
                    className="input-neo" placeholder="e.g., Spring Estate Sale" required data-testid="past-sale-title-input" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Category</label>
                  <Select value={form.category} onValueChange={(v) => updateField('category', v)}>
                    <SelectTrigger className="border-2 border-[#004E89] rounded-xl shadow-[2px_2px_0px_#004E89] h-11 font-bold" data-testid="past-sale-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-2 border-[#004E89] rounded-xl shadow-[4px_4px_0px_#004E89]">
                      <SelectItem value="estate">Estate Sale</SelectItem>
                      <SelectItem value="garage">Garage Sale</SelectItem>
                      <SelectItem value="auction">Auction</SelectItem>
                      <SelectItem value="moving">Moving Sale</SelectItem>
                      <SelectItem value="business_closing">Business Going Out</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">City</label>
                  <input value={form.city} onChange={(e) => updateField('city', e.target.value)}
                    className="input-neo" placeholder="City" data-testid="past-sale-city-input" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">State</label>
                  <input value={form.state} onChange={(e) => updateField('state', e.target.value)}
                    className="input-neo" placeholder="State" data-testid="past-sale-state-input" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Sale Date</label>
                  <input type="date" value={form.sale_date} onChange={(e) => updateField('sale_date', e.target.value)}
                    className="input-neo" data-testid="past-sale-date-input" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Description</label>
                <textarea value={form.description} onChange={(e) => updateField('description', e.target.value)}
                  className="input-neo min-h-[80px] resize-y" placeholder="Brief description of the sale..." data-testid="past-sale-desc-input" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Highlights</label>
                <input value={form.highlights} onChange={(e) => updateField('highlights', e.target.value)}
                  className="input-neo" placeholder="e.g., 200+ visitors, sold out in 2 hours!" data-testid="past-sale-highlights-input" />
              </div>
              {/* Photos */}
              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Photos</label>
                <div className="flex gap-3 items-center flex-wrap">
                  <button type="button" onClick={() => fileInputRef.current?.click()}
                    className="w-20 h-20 rounded-xl border-2 border-dashed border-[#004E89] flex items-center justify-center hover:bg-[#FFD166] hover:bg-opacity-20 transition-colors" data-testid="past-sale-add-photo-btn">
                    <Camera size={24} weight="duotone" className="text-[#5C7A99]" />
                  </button>
                  <input ref={fileInputRef} type="file" multiple accept="image/*" onChange={handlePhotoSelect} className="hidden" />
                  {photos.map((p, idx) => (
                    <div key={idx} className="relative w-20 h-20 rounded-xl overflow-hidden border-2 border-[#004E89]">
                      <img src={p.preview} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removePhoto(idx)} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center">
                        <X size={10} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={submitting} className="btn-primary py-2.5 px-6" data-testid="past-sale-submit-btn">
                  {submitting ? 'Saving...' : 'Save Past Sale'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="btn-outline py-2.5 px-6">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Past Sales Grid */}
        {loading ? (
          <div className="text-center py-20 text-[#5C7A99] font-medium">Loading past sales...</div>
        ) : pastSales.length === 0 ? (
          <div className="text-center py-16" data-testid="no-past-sales">
            <Clock size={48} weight="duotone" className="mx-auto text-[#5C7A99] mb-4" />
            <p className="text-lg font-bold text-[#004E89] mb-2">No past sales yet</p>
            <p className="text-[#5C7A99] text-sm">
              {isPremium ? 'Add your completed sales to build your reputation' : 'Upgrade to Pro to showcase your sale history'}
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {pastSales.map((sale, i) => (
              <div key={sale.id} style={{ animationDelay: `${i * 0.05}s` }} className="fade-in-up">
                <PastSaleCard sale={sale} onDelete={handleDelete} isOwner={user && (user._id === sale.created_by || user.role === 'admin')} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
